import React, { useEffect, useMemo, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import MapFilter from "components/MapFilter";
import Map from "components/Map";
import TimeProgressBar from "../components/TimeProgressBar";
import VirtualizedDataView from "../components/VirtualizedDataView";
import LoadingSpinner from "../components/LoadingSpinner";
import { 
  loadDataProgressive, 
  decompressData, 
  cleanupChunkCache,
  testDataLoading 
} from "../utils/dataLoader";
import { 
  getCachedData, 
  setCachedData, 
  hasCachedData,
  removeCachedData,
  clearCache
} from "../utils/dataCache";

export default function CryptoEmission() {
  const [viewOption, setViewOption] = useState("regular");
  const [data, setData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [pools, setPools] = useState({});
  const [selectedPools, setSelectedPools] = useState(null);
  const [filterSettings, setFilterSettings] = useState({
    type: "carbon",
    markersOn: true,
    heatmapOn: true,
  });

  // Use ref to track the current loading operation
  const loadingOperationRef = React.useRef(0);

  // Initialize dates with consistent local midnight formatting
  const getInitialDate = (year, month, day) => {
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);
    return date;
  };

  const [startDate, setStartDate] = useState(getInitialDate(2021, 1, 1));
  const [endDate, setEndDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
  });
  const [currentDate, setCurrentDate] = useState(getInitialDate(2021, 1, 1));
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [maxDensity, setMaxDensity] = useState(0);
  const [fixedMaxDensity, setFixedMaxDensity] = useState(0); // Fixed max for cumulative view
  const [showDataView, setShowDataView] = useState(false);
  const [hasInitialData, setHasInitialData] = useState(false);

  // Calculate max density efficiently
  const calculateMaxDensity = useCallback((fetchData, type, viewOption) => {
    if (!fetchData || fetchData.length === 0) return 0;
    
    if (viewOption === "regular") {
      const densityArray = type === "carbon" 
        ? fetchData.map(location => location.co2e_emissions) 
        : fetchData.map(location => location.electricity_usage);
      return Math.ceil(Math.max(...densityArray));
    } else {
      // For cumulative view, calculate from the filtered data
      const densityArray = type === "carbon" 
        ? fetchData.map(location => location.co2e_emissions) 
        : fetchData.map(location => location.electricity_usage);
      return Math.ceil(Math.max(...densityArray));
    }
  }, []);

  // Fallback data loading function
  const loadFallbackData = async (start, end) => {
    console.log('Loading fallback data from individual files...');
    console.log('🔍 Debug: loadFallbackData called with start:', start, 'end:', end);
    const allData = [];
    
    try {
      // Generate dates between start and end
      const dates = [];
      const current = new Date(start);
      const endDate = new Date(end);
      
      while (current <= endDate) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
      
      console.log('🔍 Debug: Generated dates:', dates);
      
      // Load each date individually
      for (const date of dates) {
        try {
          const url = `${process.env.PUBLIC_URL || ''}/resources/data_${date}.json?_t=${Date.now()}`;
          console.log('🔍 Debug: Loading fallback data from:', url);
          
          const response = await fetch(url, {
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          
          console.log(`🔍 Debug: Response for ${date}:`, response.status, response.statusText);
          
          if (response.ok) {
            const data = await response.json();
            console.log(`🔍 Debug: Raw data for ${date}:`, typeof data, Array.isArray(data) ? data.length : 'not array');
            
            if (Array.isArray(data)) {
              allData.push(...data);
              console.log(`🔍 Debug: Loaded ${data.length} records for ${date}`);
            } else {
              console.warn(`🔍 Debug: Data for ${date} is not an array:`, data);
            }
          } else {
            console.warn(`🔍 Debug: Failed to load ${date}, status:`, response.status);
          }
        } catch (error) {
          console.warn(`🔍 Debug: Failed to load data for ${date}:`, error);
        }
      }
      
      console.log('🔍 Debug: Fallback loading complete. Total records:', allData.length);
      return allData;
      
    } catch (error) {
      console.error('🔍 Debug: Fallback loading failed:', error);
      return [];
    }
  };

  // Process and set data function
  const processAndSetData = useCallback((rawData) => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      console.error('Invalid data for processing:', rawData);
      return;
    }

    try {
      // Process and cache the data
      const processedData = rawData.map(location => {
        // Pre-calculate totals for better performance
        const totalElectricity = location.servers_at_location.reduce((sum, server) => sum + server.electricity_usage, 0);
        const totalCO2 = location.servers_at_location.reduce((sum, server) => sum + server.co2e_emissions, 0);
        
        return {
          ...location,
          location_of_servers: {
            ...location.location_of_servers,
            longitude: Number(location.location_of_servers.longitude),
            latitude: Number(location.location_of_servers.latitude)
          },
          pools: new Set(location.servers_at_location.map(server => server.blockchain_pool_name)),
          totalElectricity,
          totalCO2
        };
      });

      console.log('Data processed successfully:', processedData.length, 'locations');

      // Calculate pools data efficiently
      const poolsData = processedData.reduce((accumulator, location) => {
        location.servers_at_location.forEach((server) => {
          if (!(server.blockchain_pool_name in accumulator)) {
            accumulator[server.blockchain_pool_name] = {
              electricity_usage: 0,
              co2e_emissions: 0,
            };
          }
          accumulator[server.blockchain_pool_name].electricity_usage += server.electricity_usage;
          accumulator[server.blockchain_pool_name].co2e_emissions += server.co2e_emissions;
        });
        return accumulator;
      }, {});

      console.log('Pools data calculated:', Object.keys(poolsData).length, 'pools');

      setPools(poolsData);
      setData(processedData);
      setOriginalData(processedData);

      // Cache the processed data
      const cacheKey = `data_${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}`;
      setCachedData(cacheKey, processedData);
      
    } catch (error) {
      console.error('Data processing failed:', error);
    }
  }, [startDate, endDate]);

  // Calculate fixed maximum density for cumulative view (based on entire date range)
  useEffect(() => {
    if (!originalData || originalData.length === 0) return;

    if (viewOption === "cumulative") {
      // Calculate cumulative data up to the END date to get the fixed maximum
      const normalizeDate = (date) => {
        const newDate = new Date(date);
        newDate.setUTCHours(0, 0, 0, 0);
        return newDate;
      };

      const normalizedStartDate = normalizeDate(startDate);
      const normalizedEndDate = normalizeDate(endDate);

      // Group data by location and accumulate values over the entire date range
      const cumulativeData = {};

      originalData.forEach((entry) => {
        const entryDate = normalizeDate(entry.date);

        // Include all data from start date to END date
        if (entryDate >= normalizedStartDate && entryDate <= normalizedEndDate) {
          const locationName = entry.location_of_servers.location_name;

          if (!cumulativeData[locationName]) {
            // Initialize with zero values
            cumulativeData[locationName] = {
              co2e_emissions: 0,
              electricity_usage: 0,
            };
          }

          // Accumulate total values for the location (these are daily totals from the source data)
          cumulativeData[locationName].co2e_emissions += entry.co2e_emissions;
          cumulativeData[locationName].electricity_usage += entry.electricity_usage;
        }
      });

      const fullRangeData = Object.values(cumulativeData);

      // Calculate max density from the full range data
      const type = filterSettings.type;
      const densityArray = type === "carbon"
        ? fullRangeData.map(location => location.co2e_emissions || 0)
        : fullRangeData.map(location => location.electricity_usage || 0);

      const fixedMax = Math.ceil(Math.max(...densityArray, 0));
      console.log(`Fixed max density for cumulative view (entire range): ${fixedMax} for ${type}`);
      setFixedMaxDensity(fixedMax);
    }
  }, [originalData, viewOption, startDate, endDate, filterSettings.type]);

  // Update max density when data changes
  useEffect(() => {
    if (originalData) {
      if (viewOption === "regular") {
        const maxDensity = calculateMaxDensity(originalData, filterSettings.type, viewOption);
        setMaxDensity(maxDensity);
      }
      // For cumulative view, maxDensity will be calculated from filteredData in the other useEffect
    }
  }, [viewOption, originalData, filterSettings.type, calculateMaxDensity]);



  // Progressive data loading with caching
  const getData = useCallback(async () => {
    if (!startDate || !endDate) return;

    // Increment operation ID to invalidate previous operations
    loadingOperationRef.current += 1;
    const currentOperationId = loadingOperationRef.current;

    setLoading(true);
    setLoadingProgress(0);

    try {
      // Check cache first
      const cacheKey = `data_${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}`;

      if (hasCachedData(cacheKey)) {
        console.log('🔍 Debug: Loading data from cache:', cacheKey);
        const cachedData = getCachedData(cacheKey);
        console.log('🔍 Debug: Raw cached data:', cachedData);
        console.log('🔍 Debug: Cached data type:', typeof cachedData);
        
        // Validate cached data before processing
        if (!cachedData) {
          console.warn('🔍 Debug: Cached data is null/undefined, removing from cache and loading fresh');
          removeCachedData(cacheKey);
        } else {
          try {
            console.log('🔍 Debug: About to decompress cached data...');
            const decompressedData = decompressData(cachedData);
            console.log('🔍 Debug: Decompressed data:', decompressedData);
            console.log('🔍 Debug: Decompressed data type:', typeof decompressedData);
            console.log('🔍 Debug: Decompressed data is array:', Array.isArray(decompressedData));
            
            // Validate decompressed data
            if (Array.isArray(decompressedData) && decompressedData.length > 0) {
              console.log('🔍 Debug: Successfully loaded cached data:', decompressedData.length, 'records');
              // Only update state if this is still the current operation
              if (loadingOperationRef.current === currentOperationId) {
                setData(decompressedData);
                setOriginalData(decompressedData);
                setHasInitialData(true);
                setLoading(false);
              }
              return;
            } else {
              console.warn('🔍 Debug: Cached data decompression failed or returned empty array, removing from cache');
              removeCachedData(cacheKey);
            }
          } catch (decompressError) {
            console.error('🔍 Debug: Error decompressing cached data:', decompressError);
            console.warn('🔍 Debug: Removing corrupted cache entry');
            removeCachedData(cacheKey);
          }
        }
      }

      console.log('Starting progressive data loading for date range:', startDate, 'to', endDate);
      
      // Progressive loading with progress tracking
      const dateRange = { startDate, endDate };
      
      console.log('🔍 Debug: About to call loadDataProgressive with dateRange:', dateRange);
      const fetchData = await loadDataProgressive(
        dateRange,
        (progress) => {
          console.log('🔍 Debug: Loading progress:', Math.round(progress * 100) + '%');
          // Only update progress if this is still the current operation
          if (loadingOperationRef.current === currentOperationId) {
            setLoadingProgress(progress);
          }
        },
        (chunkData, dateChunk) => {
          console.log(`🔍 Debug: Loaded chunk for dates: ${dateChunk.join(', ')} with ${chunkData.length} records`);
        }
      );

      console.log('🔍 Debug: Total data loaded from progressive loading:', fetchData);
      console.log('🔍 Debug: fetchData type:', typeof fetchData);
      console.log('🔍 Debug: fetchData is array:', Array.isArray(fetchData));
      if (Array.isArray(fetchData)) {
        console.log('🔍 Debug: fetchData length:', fetchData.length);
      }

      if (!fetchData || fetchData.length === 0) {
        console.warn('No data received from progressive loading');
        setLoading(false);
        return;
      }

      // Validate data structure before processing
      if (!Array.isArray(fetchData)) {
        console.error('fetchData is not an array:', fetchData);
        throw new Error('Invalid data structure received from server');
      }
      
      // Process and cache the data
      const processedData = fetchData.map(location => {
        // Validate location structure
        if (!location || !location.servers_at_location || !Array.isArray(location.servers_at_location)) {
          console.error('Invalid location structure:', location);
          throw new Error('Invalid location data structure');
        }
        
        // Pre-calculate totals for better performance
        const totalElectricity = location.servers_at_location.reduce((sum, server) => sum + server.electricity_usage, 0);
        const totalCO2 = location.servers_at_location.reduce((sum, server) => sum + server.co2e_emissions, 0);
        
        return {
          ...location,
          location_of_servers: {
            ...location.location_of_servers,
            longitude: Number(location.location_of_servers.longitude),
            latitude: Number(location.location_of_servers.latitude)
          },
          pools: new Set(location.servers_at_location.map(server => server.blockchain_pool_name)),
          totalElectricity,
          totalCO2
        };
      });

      console.log('Data processed successfully:', processedData.length, 'locations');

      // Calculate pools data efficiently
      const poolsData = processedData.reduce((accumulator, location) => {
        location.servers_at_location.forEach((server) => {
          if (!(server.blockchain_pool_name in accumulator)) {
            accumulator[server.blockchain_pool_name] = {
              electricity_usage: 0,
              co2e_emissions: 0,
            };
          }
          accumulator[server.blockchain_pool_name].electricity_usage += server.electricity_usage;
          accumulator[server.blockchain_pool_name].co2e_emissions += server.co2e_emissions;
        });
        return accumulator;
      }, {});

      console.log('Pools data calculated:', Object.keys(poolsData).length, 'pools');

      // Only update state if this is still the current operation
      if (loadingOperationRef.current === currentOperationId) {
        setPools(poolsData);
        setData(processedData);
        setOriginalData(processedData);
        setHasInitialData(true);

        // Cache the processed data
        setCachedData(cacheKey, processedData);
      } else {
        console.log('🔍 Debug: Discarding data from outdated operation');
      }

    } catch (error) {
      console.error('Failed to load data:', error);
      
      // Clear potentially corrupted cache and try to load fresh data
      console.log('Clearing potentially corrupted cache and attempting fresh data load...');
      clearCache();
      
      // Try to load from individual files as fallback
      try {
        console.log('Attempting fallback data loading from individual files...');
        const fallbackData = await loadFallbackData(startDate, endDate);
        
        if (fallbackData && fallbackData.length > 0) {
          console.log('Fallback data loaded successfully:', fallbackData.length, 'records');
          
          // Process the fallback data
          const processedFallbackData = fallbackData.map(location => {
            if (!location || !location.servers_at_location || !Array.isArray(location.servers_at_location)) {
              console.warn('Skipping invalid location in fallback data:', location);
              return null;
            }
            
            const totalElectricity = location.servers_at_location.reduce((sum, server) => sum + server.electricity_usage, 0);
            const totalCO2 = location.servers_at_location.reduce((sum, server) => sum + server.co2e_emissions, 0);
            
            return {
              ...location,
              location_of_servers: {
                ...location.location_of_servers,
                longitude: Number(location.location_of_servers.longitude),
                latitude: Number(location.location_of_servers.latitude)
              },
              pools: new Set(location.servers_at_location.map(server => server.blockchain_pool_name)),
              totalElectricity,
              totalCO2
            };
          }).filter(Boolean); // Remove null entries
          
          if (processedFallbackData.length > 0) {
            console.log('Setting fallback data:', processedFallbackData.length, 'valid records');

            // Only update state if this is still the current operation
            if (loadingOperationRef.current === currentOperationId) {
              setData(processedFallbackData);
              setOriginalData(processedFallbackData);

              // Calculate pools data for fallback
              const poolsData = processedFallbackData.reduce((accumulator, location) => {
                location.servers_at_location.forEach((server) => {
                  if (!(server.blockchain_pool_name in accumulator)) {
                    accumulator[server.blockchain_pool_name] = {
                      electricity_usage: 0,
                      co2e_emissions: 0,
                    };
                  }
                  accumulator[server.blockchain_pool_name].electricity_usage += server.electricity_usage;
                  accumulator[server.blockchain_pool_name].co2e_emissions += server.co2e_emissions;
                });
                return accumulator;
              }, {});

              setPools(poolsData);
              setHasInitialData(true);
            }
          } else {
            console.error('No valid fallback data available after processing');
          }
        } else {
          console.error('No fallback data available');
        }
      } catch (fallbackError) {
        console.error('Fallback data loading also failed:', fallbackError);
      }
    } finally {
      // Only reset loading state if this is still the current operation
      if (loadingOperationRef.current === currentOperationId) {
        setLoading(false);
        setLoadingProgress(0);
      }
    }
  }, [startDate, endDate]);

  // Load data when dates change
  useEffect(() => {
    getData();
  }, [getData]);

  // Initial data loading on component mount
  useEffect(() => {
    // Force initial data load when component mounts
    console.log('Component mounted, loading initial data...');
    
    // Add debug logging to see what's happening
    console.log('🔍 Debug: Starting initial data load');
    console.log('🔍 Debug: startDate:', startDate);
    console.log('🔍 Debug: endDate:', endDate);
    console.log('🔍 Debug: PUBLIC_URL:', process.env.PUBLIC_URL);
    
    getData();
  }, []); // Empty dependency array means this runs only once on mount

  // Handle filter changes efficiently
  const handleFilterChange = useCallback((key, value) => {
    if (key === 'startDate' || key === 'endDate') {
      // For date changes, update both filterSettings and the main date state
      setFilterSettings((previous) => ({
        ...previous,
        [key]: value,
      }));
      
      if (key === 'startDate') {
        setStartDate(value);
        setCurrentDate(value); // Keep currentDate synchronized with startDate
        console.log('Start date updated via filter change:', value.toISOString().split('T')[0]);
      } else if (key === 'endDate') {
        setEndDate(value);
        console.log('End date updated via filter change:', value.toISOString().split('T')[0]);
      }
      
      // Clean up old chunk cache when changing dates
      cleanupChunkCache();
    } else {
      // For non-date filters, just update filterSettings
      setFilterSettings((previous) => ({
        ...previous,
        [key]: value,
      }));
    }
  }, []);

  // Efficient data filtering with memoization
  const filteredData = useMemo(() => {
    console.log('filteredData useMemo triggered - originalData length:', originalData?.length, 'currentDate:', currentDate?.toISOString().split('T')[0], 'viewOption:', viewOption);
    if (!originalData) return [];

    const normalizeDate = (date) => {
      const newDate = new Date(date);
      newDate.setUTCHours(0, 0, 0, 0);
      return newDate;
    };

    const normalizedCurrentDate = normalizeDate(currentDate);
    const normalizedStartDate = normalizeDate(startDate);

    if (viewOption === "regular") {
      return originalData.filter(
        (entry) => normalizeDate(entry.date).getTime() === normalizedCurrentDate.getTime()
      );
    } else {
      // Cumulative view: sum up data from start date to current date
      console.log('Processing cumulative view from', normalizedStartDate, 'to', normalizedCurrentDate);

      // Group data by location and accumulate values over time
      const cumulativeData = {};

      originalData.forEach((entry) => {
        const entryDate = normalizeDate(entry.date);

        // Only include data from start date to current date
        if (entryDate >= normalizedStartDate && entryDate <= normalizedCurrentDate) {
          const locationName = entry.location_of_servers.location_name;

          if (!cumulativeData[locationName]) {
            // Initialize location data - create a clean object without spreading entry
            cumulativeData[locationName] = {
              date: normalizedCurrentDate, // Use current date for cumulative view
              location_of_servers: {
                longitude: entry.location_of_servers.longitude,
                latitude: entry.location_of_servers.latitude,
                location_name: entry.location_of_servers.location_name
              },
              servers_at_location: [],
              pools: new Set(),
              co2e_emissions: 0,
              electricity_usage: 0,
              totalCO2: 0,
              totalElectricity: 0,
              dateRange: {
                start: entryDate,
                end: entryDate
              }
            };
          }

          // Accumulate server data by pool
          entry.servers_at_location.forEach((server) => {
            const existingServerIndex = cumulativeData[locationName].servers_at_location.findIndex(
              existing => existing.blockchain_pool_name === server.blockchain_pool_name
            );

            if (existingServerIndex === -1) {
              // New pool/server, add it with accumulated values
              cumulativeData[locationName].servers_at_location.push({
                blockchain_pool_name: server.blockchain_pool_name,
                electricity_usage: server.electricity_usage,
                co2e_emissions: server.co2e_emissions
              });
              cumulativeData[locationName].pools.add(server.blockchain_pool_name);
            } else {
              // Existing pool/server, accumulate values
              cumulativeData[locationName].servers_at_location[existingServerIndex].electricity_usage += server.electricity_usage;
              cumulativeData[locationName].servers_at_location[existingServerIndex].co2e_emissions += server.co2e_emissions;
            }
          });

          // Accumulate total values for the location
          cumulativeData[locationName].co2e_emissions += entry.co2e_emissions;
          cumulativeData[locationName].electricity_usage += entry.electricity_usage;
          cumulativeData[locationName].totalCO2 += entry.co2e_emissions;
          cumulativeData[locationName].totalElectricity += entry.electricity_usage;

          // Update date range
          if (entryDate < cumulativeData[locationName].dateRange.start) {
            cumulativeData[locationName].dateRange.start = entryDate;
          }
          if (entryDate > cumulativeData[locationName].dateRange.end) {
            cumulativeData[locationName].dateRange.end = entryDate;
          }
        }
      });

      const result = Object.values(cumulativeData);
      console.log('Cumulative data processed:', result.length, 'locations with accumulated values');

      // Enhanced debugging: Track a specific location across time
      if (result.length > 0) {
        const sampleLocation = result[0];
        console.log(`[CUMULATIVE DEBUG] Date: ${normalizedCurrentDate.toISOString().split('T')[0]}`);
        console.log('Sample location data:', {
          name: sampleLocation.location_of_servers.location_name,
          co2e_emissions: sampleLocation.co2e_emissions,
          electricity_usage: sampleLocation.electricity_usage,
          totalCO2: sampleLocation.totalCO2,
          totalElectricity: sampleLocation.totalElectricity
        });

        // Find a commonly occurring location for better debugging
        const testLocation = result.find(loc => loc.location_of_servers.location_name.includes('China') || loc.location_of_servers.location_name.includes('Hangzhou'));
        if (testLocation) {
          console.log(`[CUMULATIVE DEBUG] ${testLocation.location_of_servers.location_name} - CO2: ${testLocation.co2e_emissions}, Electricity: ${testLocation.electricity_usage}`);
        }
      }

      return result;
    }
  }, [originalData, currentDate, viewOption, startDate]);

  // Apply pool filtering
  useEffect(() => {
    console.log('Data update useEffect triggered - selectedPools:', selectedPools, 'filteredData length:', filteredData?.length, 'currentDate:', currentDate?.toISOString().split('T')[0]);
    if (selectedPools && filteredData.length) {
      const filtered = filteredData.filter((entry) => {
        for (const key in selectedPools) {
          if (selectedPools[key] && entry.pools.has(key)) {
            return true;
          }
        }
        return false;
      });
      console.log('Setting filtered data, length:', filtered.length);
      setData(filtered);
    } else {
      console.log('Setting full filteredData, length:', filteredData?.length);
      setData(filteredData);
    }
  }, [selectedPools, filteredData, currentDate]);

  // Debug: Log when filtered data changes
  useEffect(() => {
    if (filteredData && filteredData.length > 0) {
      const totalCO2 = filteredData.reduce((sum, entry) => sum + (entry.co2e_emissions || 0), 0);
      const totalElectricity = filteredData.reduce((sum, entry) => sum + (entry.electricity_usage || 0), 0);
      
      console.log(`Filtered data updated - View: ${viewOption}, Current Date: ${currentDate.toISOString().split('T')[0]}`);
      console.log(`Total locations: ${filteredData.length}, Total CO₂: ${totalCO2.toFixed(2)}, Total Electricity: ${totalElectricity.toFixed(2)}`);
      
      if (viewOption === "cumulative") {
        console.log('Cumulative view data sample:', filteredData.slice(0, 3).map(loc => ({
          location: loc.location_of_servers.location_name,
          co2: loc.co2e_emissions,
          electricity: loc.electricity_usage
        })));
      }
    }
  }, [filteredData, viewOption, currentDate]);

  // Update max density for cumulative view when filtered data changes
  useEffect(() => {
    console.log('MaxDensity useEffect triggered - viewOption:', viewOption, 'filteredData length:', filteredData?.length, 'currentDate:', currentDate?.toISOString().split('T')[0]);
    if (viewOption === "cumulative" && filteredData && filteredData.length > 0) {
      const type = filterSettings.type;
      console.log('Calculating maxDensity for cumulative view, type:', type);
      
      const densityArray = type === "carbon" 
        ? filteredData.map(location => location.co2e_emissions || 0)
        : filteredData.map(location => location.electricity_usage || 0);
      
      console.log('Density array:', densityArray);
      console.log('Max value in density array:', Math.max(...densityArray));
      
      const maxDensity = Math.ceil(Math.max(...densityArray));
      console.log(`Cumulative max density updated: ${maxDensity} for ${type}`);
      setMaxDensity(maxDensity);
    } else if (viewOption === "regular") {
      console.log('Regular view - not updating maxDensity here');
    } else {
      console.log('No filteredData or not cumulative view');
    }
  }, [filteredData, viewOption, filterSettings.type, currentDate]);

  // Synchronize currentDate with startDate when startDate changes
  useEffect(() => {
    if (startDate && currentDate && currentDate < startDate) {
      console.log('Synchronizing currentDate with startDate:', startDate.toISOString().split('T')[0]);
      setCurrentDate(startDate);
    }
  }, [startDate, currentDate]);

  // Synchronize filterSettings dates with main date state
  useEffect(() => {
    setFilterSettings(prev => ({
      ...prev,
      startDate: startDate,
      endDate: endDate
    }));
  }, [startDate, endDate]);

  // Handle date changes
  const handleDateChange = useCallback((start, end) => {
    // Normalize dates to ensure consistency (local midnight)
    const normalizeDate = (date) => {
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      return newDate;
    };
    
    const normalizedStart = normalizeDate(start);
    const normalizedEnd = normalizeDate(end);
    
    console.log('Date change - Start:', normalizedStart.toISOString().split('T')[0], 'End:', normalizedEnd.toISOString().split('T')[0]);
    
    setStartDate(normalizedStart);
    setEndDate(normalizedEnd);
    // Reset current date to the new start date to keep progress bar synchronized
    setCurrentDate(normalizedStart);
    
    // Clean up old chunk cache when changing dates
    cleanupChunkCache();
  }, []);

  // Calculate totals efficiently
  const totals = useMemo(() => {
    if (!data || data.length === 0) return { totalCO2: 0, totalElectricity: 0 };
    
    return data.reduce(
      (acc, entry) => {
        // Use pre-computed totals if available
        acc.totalCO2 += entry.totalCO2 || entry.co2e_emissions || 0;
        acc.totalElectricity += entry.totalElectricity || entry.electricity_usage || 0;
        return acc;
      },
      { totalCO2: 0, totalElectricity: 0 }
    );
  }, [data]);

  // Handle data view toggle
  const toggleDataView = useCallback(() => {
    setShowDataView(prev => !prev);
  }, []);

  // Handle data selection from virtualized view
  const handleDataSelect = useCallback((location) => {
    console.log('Selected location:', location);
    // Could implement map centering or detailed view here
  }, []);

  // Test data loading functionality
  const handleTestDataLoading = useCallback(async () => {
    console.log('Testing data loading functionality...');
    
    try {
      const result = await testDataLoading();
      if (result.success) {
        console.log('✅ Data loading test successful:', result);
        alert(`Data loading test successful!\nSingle date: ${result.singleDate} records\nChunk: ${result.chunk} records`);
      } else {
        console.error('❌ Data loading test failed:', result.error);
        alert(`Data loading test failed: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Test function error:', error);
      alert(`Test function error: ${error.message}`);
    }
  }, []);

  // Debug date consistency
  useEffect(() => {
    console.log('🔍 Date State Debug:', {
      startDate: startDate?.toISOString().split('T')[0],
      endDate: endDate?.toISOString().split('T')[0],
      currentDate: currentDate?.toISOString().split('T')[0],
      filterStartDate: filterSettings.startDate?.toISOString().split('T')[0],
      filterEndDate: filterSettings.endDate?.toISOString().split('T')[0]
    });
  }, [startDate, endDate, currentDate, filterSettings.startDate, filterSettings.endDate]);

  // No more initial loading screen - show interface immediately

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0
      }}
    >
            <Grid
        container
        alignItems="center"
        justifyContent="center"
        style={{ background: "gray", width: "100%", flex: "none" }}
      >
        {/* Logo and Project Title etc. */}
      </Grid>

      <Grid container style={{ flex: "1", overflow: "hidden", minHeight: 0, marginBottom: "0.5vh" }}>
        <Grid
          item
          container
          direction="column"
          style={{ flex: "1", overflow: "hidden", minHeight: 0 }}
        >
          <Grid item container style={{ marginBottom: "1vh", flex: "none" }}>
            <MapFilter
              settings={filterSettings}
              onFilterChange={handleFilterChange}
              onDateChange={handleDateChange}
              onPoolSelection={setSelectedPools}
              startDate={startDate}
              endDate={endDate}
              totalCO2={totals.totalCO2}
              totalElectricity={totals.totalElectricity}
              pools={pools}
              loading={loading}
              loadingProgress={loadingProgress}
              onToggleDataView={toggleDataView}
              showDataView={showDataView}
              onTestDataLoading={handleTestDataLoading}
            />
          </Grid>

          <Grid item style={{ flex: "1", minHeight: 0, height: "100%" }}>
            {console.log('Rendering map section - showDataView:', showDataView, 'loading:', loading, 'hasInitialData:', hasInitialData)}
            {showDataView ? (
              <VirtualizedDataView
                data={data}
                onDataSelect={handleDataSelect}
                viewOption={viewOption}
                filterSettings={filterSettings}
                loading={loading}
                loadingProgress={loadingProgress}
              />
            ) : loading ? (
              // Show a simple loading placeholder instead of the Map component
              <div style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(240, 248, 255, 0.3)",
                border: "1px solid #e0e0e0",
                borderRadius: "4px"
              }}>
                <div style={{
                  textAlign: "center",
                  padding: "20px"
                }}>
                  <LoadingSpinner 
                    variant="ripple" 
                    size="medium" 
                    text="Loading Map..." 
                    showProgress={true}
                    progress={loadingProgress * 100}
                    className="text-blue-600"
                  />
                  <div style={{
                    marginTop: "10px",
                    fontSize: "14px",
                    color: "#666"
                  }}>
                    Preparing map visualization... {Math.round(loadingProgress * 100)}%
                  </div>
                </div>
              </div>
            ) : (
              <Map
                data={data}
                settings={filterSettings}
                totalCO2={totals.totalCO2}
                totalElectricity={totals.totalElectricity}
                isLoading={false}
                maxDensity={maxDensity}
                fixedMaxDensity={fixedMaxDensity}
                viewOption={viewOption}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <TimeProgressBar
        startDate={startDate}
        endDate={endDate}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        setViewOption={setViewOption}
        loading={loading}
        currentTotals={totals}
      />

      {/* Debug component for troubleshooting */}
    </div>
  );
}

